import { ExamCenterProctorType } from '@exam-center/graphql';
import { PROCTOR_TYPE_TEXTS } from '@exam-center/proctor-management';
import {
  ProctorDocumentFields,
  ProctorEmployeeInformationFields,
  ProctorPersonalInformationFields,
  ProctorTypeField,
} from '@exam-center/proctor-request-management';
import { Divider, Flex, Form, FormButton, PaperSection, type FormProps } from '@shared/components';
import { ENGLISH_TITLE_OPTIONS, formatDate, THAI_TITLE_OPTIONS } from '@shared/utils';

interface InputFieldProps {
  name: string;
}

export type AdminProctorDetailsFormProps = FormProps & {
  fields: {
    personal: {
      titleTh: InputFieldProps;
      firstNameTh: InputFieldProps;
      lastNameTh: InputFieldProps;
      titleEn: InputFieldProps;
      firstNameEn: InputFieldProps;
      lastNameEn: InputFieldProps;
      email: InputFieldProps;
      phoneNumber: InputFieldProps;
      address: InputFieldProps;
    };
    employeeInfo: {
      code: InputFieldProps;
      position: InputFieldProps;
      department: InputFieldProps;
      termination: {
        isAssigned: InputFieldProps;
        endedAt: InputFieldProps;
      };
    };
  };
  values: {
    idNumber: string;
    proctorType: ExamCenterProctorType;
    email: string;
    agreementStorageObjectsFile: {
      name: string;
      mode?: 'download' | 'preview';
      value: {
        url: string;
        name: string;
      };
    }[];
    startedAt?: Date;
    termination: {
      isAssigned: boolean;
      endedAt?: Date;
    };
  };
  options?: {
    shouldShowEndedAtDateDisplay?: boolean;
  };
  isSubmitting?: boolean;
};

export default function ProctorRequestForm(props: Readonly<AdminProctorDetailsFormProps>) {
  const { fields, options, isSubmitting, values, ...restProps } = props;
  const isProctorType3 = values.proctorType === ExamCenterProctorType.Proctor_3;
  return (
    <Form {...restProps}>
      <PaperSection title='ข้อมูลผู้คุมสอบ'>
        <Flex
          sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(6), p: theme.spacing(6) })}
        >
          <ProctorTypeField
            fields={{
              proctorType: {
                mode: 'view',
                value: <>{PROCTOR_TYPE_TEXTS[values.proctorType]}</>,
              },
            }}
          />
          <Divider />
          <ProctorPersonalInformationFields
            fields={{
              idNumber: { mode: 'view', value: values.idNumber },
              titleTh: {
                mode: 'input',
                name: fields.personal.titleTh.name,
                slotProps: { options: THAI_TITLE_OPTIONS },
              },
              firstNameTh: { mode: 'input', name: fields.personal.firstNameTh.name },
              lastNameTh: { mode: 'input', name: fields.personal.lastNameTh.name },
              titleEn: {
                mode: 'input',
                name: fields.personal.titleEn.name,
                slotProps: { options: ENGLISH_TITLE_OPTIONS, required: !isProctorType3 },
              },
              firstNameEn: {
                mode: 'input',
                name: fields.personal.firstNameEn.name,
                slotProps: { required: !isProctorType3 },
              },
              lastNameEn: {
                mode: 'input',
                name: fields.personal.lastNameEn.name,
                slotProps: { required: !isProctorType3 },
              },
              email: {
                mode: isProctorType3 ? 'input' : 'view',
                name: fields.personal.email.name,
                value: isProctorType3 ? '' : values.email,
              },
              phoneNumber: { mode: 'input', name: fields.personal.phoneNumber.name },
              address: { mode: 'input', name: fields.personal.address.name },
            }}
          />
          <Divider />
          <ProctorEmployeeInformationFields
            fields={{
              code: { mode: 'input', name: fields.employeeInfo.code.name },
              position: { mode: 'input', name: fields.employeeInfo.position.name },
              department: { mode: 'input', name: fields.employeeInfo.department.name },
              startedAt: {
                mode: 'view',
                value: formatDate(values.startedAt),
              },
              isTerminationAssign: {
                mode: isProctorType3 ? 'view' : 'input',
                name: fields.employeeInfo.termination.isAssigned.name,
                value: isProctorType3 ? <>{values.termination.isAssigned}</> : <></>,
              },
              endedAt: {
                mode: isProctorType3 ? 'view' : 'input',
                name: fields.employeeInfo.termination.endedAt.name,
                value: isProctorType3 ? formatDate(values.termination.endedAt) : '-',
                slotProps: {
                  label: 'วันที่สิ้นสุดปฏิบัติหน้าที่',
                },
              },
            }}
            options={{ shouldShowEndedAtDateDisplay: options?.shouldShowEndedAtDateDisplay }}
          />
          <Divider />
          <ProctorDocumentFields
            fields={{
              agreementForms: {
                mode: 'view',
                slotProps: {
                  files: values.agreementStorageObjectsFile,
                },
              },
            }}
            hideIdentificationFiles={true}
          />
        </Flex>
      </PaperSection>
      <Flex
        sx={(theme) => ({
          gap: theme.spacing(2.5),
          justifyContent: 'flex-end',
          mt: theme.spacing(6),
        })}
      >
        <FormButton label='บันทึก' type='submit' loading={isSubmitting} />
      </Flex>
    </Form>
  );
}
